/* =========================================================
   BOMBER JACKET CUSTOMIZER — Shopify Edition
   Orlaven custom design and Shopify cart integration:
   - SVG recoloring (body/pockets/collar/trims/zip/buttons)
   - 4 views (front/back/left/right) with thumbnails
   - 13 design placements: text / letter / image upload
   - Fixed Shopify product price
   - Sizes (Men/Women/Youth/Kids) + Custom measurements
   - Add to Cart via Shopify AJAX API (line item properties)
   ========================================================= */
(function () {
  "use strict";

  function formatConfiguredPrice(config) {
    if (config.formattedPrice) return config.formattedPrice;
    try {
      return new Intl.NumberFormat(undefined, {
        style: "currency",
        currency: config.currency || "USD"
      }).format((Number(config.price) || 0) / 100);
    } catch (e) {
      return "$" + ((Number(config.price) || 0) / 100).toFixed(2);
    }
  }

  function formatMeasurement(cm, unit) {
    return unit === "in" ? String(Number((Number(cm) / 2.54).toFixed(1))) : String(cm);
  }

  function normalizedSize(value) {
    return String(value || "").trim().toLowerCase().replace(/\s+/g, "");
  }

  function variantMatchesSize(variant, size) {
    var wanted = normalizedSize(size);
    return String(variant.title || "").split("/").some(function (part) {
      return normalizedSize(part) === wanted;
    });
  }

  function baseVariant(config) {
    var variants = Array.isArray(config.variants) ? config.variants : [];
    var selected = variants.filter(function (variant) {
      return String(variant.id) === String(config.variantId) && variant.available !== false;
    })[0];
    return selected || variants.filter(function (variant) { return variant.available !== false; })[0] ||
      (config.variantId ? { id: config.variantId, available: true, price: config.price } : null);
  }

  function selectVariant(size, config) {
    var variants = Array.isArray(config.variants) ? config.variants : [];
    if (!variants.length) return baseVariant(config);
    var exact = variants.filter(function (variant) {
      return variant.available !== false && variantMatchesSize(variant, size);
    })[0];
    return exact || (size === "Custom" ? baseVariant(config) : null);
  }

  function availableSizes(labels, config) {
    if (!Array.isArray(config.variants) || !config.variants.length) return labels.slice();
    return labels.filter(function (label) { return !!selectVariant(label, config); });
  }

  function validateOrder(order, config) {
    var errors = [];
    var quantity = Number(order.qty);
    if (!Number.isInteger(quantity) || quantity < 1 || quantity > 99) {
      errors.push("Quantity must be a whole number from 1 to 99.");
    }
    if (!order.gender) errors.push("Please select your Gender.");
    if (!order.size) errors.push("Please select Size.");
    else if (!selectVariant(order.size, config)) errors.push("The selected size is currently unavailable.");
    if (order.size === "Custom") {
      [
        ["chest", "Chest", 40, 220],
        ["bottom", "Bottom", 40, 220],
        ["back", "Back Length", 30, 120],
        ["sleeves", "Sleeves", 30, 120]
      ].some(function (rule) {
        var value = Number(order.custom_size[rule[0]]);
        if (!Number.isFinite(value) || value < rule[2] || value > rule[3]) {
          errors.push(rule[1] + " must be between " + rule[2] + " and " + rule[3] + " cm.");
          return true;
        }
        return false;
      });
    }
    return errors;
  }

  function createCartFormData(options) {
    var form = new FormData();
    form.set("id", String(options.variantId));
    form.set("quantity", String(options.quantity));
    Object.keys(options.properties || {}).forEach(function (key) {
      form.set("properties[" + key + "]", String(options.properties[key]));
    });
    (options.artwork || []).forEach(function (item) {
      form.set("properties[" + item.label + " (Image)]", item.file, item.filename);
      if (item.note) form.set("properties[" + item.label + " Artwork Note]", item.note);
    });
    if (options.sectionIds && options.sectionIds.length) form.set("sections", options.sectionIds.join(","));
    form.set("sections_url", options.sectionsUrl || "/");
    return form;
  }

  function artworkFileError(file) {
    if (!file) return "Please choose an artwork file.";
    if (file.size > 5 * 1024 * 1024) return "File too large — max 5 MB.";
    var extension = String(file.name || "").split(".").pop().toLowerCase();
    var expectedTypes = {
      png: ["image/png"],
      jpg: ["image/jpeg"],
      jpeg: ["image/jpeg"],
      webp: ["image/webp"],
      pdf: ["application/pdf"]
    }[extension];
    var type = String(file.type || "").toLowerCase();
    if (!expectedTypes || (type && expectedTypes.indexOf(type) === -1)) {
      return "Upload a PNG, JPG, WEBP or PDF artwork file.";
    }
    return "";
  }

  if (typeof module !== "undefined" && module.exports) {
    module.exports = {
      formatConfiguredPrice: formatConfiguredPrice,
      formatMeasurement: formatMeasurement,
      selectVariant: selectVariant,
      availableSizes: availableSizes,
      validateOrder: validateOrder,
      createCartFormData: createCartFormData,
      artworkFileError: artworkFileError
    };
  }
  if (typeof document === "undefined") return;

  var root = document.querySelector("[data-bjc]");
  if (!root) return;

  var CFG = window.BJC_CONFIG || {};
  var BASE_PRICE = 0; // set on size select
  var state = {
    gender: "men",
    material: "Nylon",
    closure: "zipper",
    pocket_style: "btn_pock",
    size: "",
    custom_size: { chest: "", bottom: "", back: "", sleeves: "" },
    colors: {
      body: { hex: "", name: "" },
      pockets: { hex: "", name: "" },
      collar: { hex: "", name: "" },
      trim_base: { hex: "", name: "" },
      trim: { hex: "", name: "" },
      trim_border: { hex: "#000000", name: "Black" },
      zip: { hex: "#d4af37", name: "Gold" },
      buttons: { hex: "", name: "" }
    },
    placements: {},
    note: "",
    qty: 1
  };

  /* ---------- Placement map: key -> svg ids + view + limits ---------- */
  var PLACEMENTS = {
    right_chest:         { label: "Right Chest",        view: "front", text: "right_chest_text",         text1: "right_chest_text1",         img: "right_chest_image",         ph: "right_chest_placeholder",         max: 12, price_text: 10, price_img: 15 },
    left_chest:          { label: "Left Chest",         view: "front", text: "left_chest_text",          text1: "left_chest_text1",          img: "left_chest_image",          ph: "left_chest_placeholder",          max: 12, price_text: 10, price_img: 15 },
    right_pocket:        { label: "Right Pocket",       view: "front", text: "right_pocket_text",        text1: "right_pocket_text1",        img: "right_pocket_image",        ph: "right_pocket_placeholder",        max: 10, price_text: 10, price_img: 15 },
    left_pocket:         { label: "Left Pocket",        view: "front", text: "left_pocket_text",         text1: "left_pocket_text1",         img: "left_pocket_image",         ph: "left_pocket_placeholder",         max: 10, price_text: 10, price_img: 15 },
    back_top:            { label: "Back Top",           view: "back",  text: "bottom_top_text",          text1: "bottom_top_text1",          img: "bottom_top_image",          ph: "bottom_top_placeholder",          max: 20, price_text: 20, price_img: 25 },
    back_middle:         { label: "Back Middle",        view: "back",  text: "bottom_middle_text",       text1: "bottom_middle_text1",       img: "bottom_middle_image",       ph: "bottom_middle_placeholder",       max: 24, price_text: 25, price_img: 30 },
    back_bottom:         { label: "Back Bottom",        view: "back",  text: "bottom_bottom_text",       text1: "bottom_bottom_text1",       img: "bottom_bottom_image",       ph: "bottom_bottom_placeholder",       max: 20, price_text: 20, price_img: 25 },
    right_sleeve_top:    { label: "Right Sleeve Top",   view: "left",  text: "right_sleeve_text",        text1: "right_sleeve_text1",        img: "right_sleeve_image",        ph: "right_sleeve_placeholder",        max: 15, price_text: 10, price_img: 15 },
    right_sleeve_middle: { label: "Right Sleeve Middle",view: "left",  text: "right_middle_sleeve_text", text1: "right_middle_sleeve_text1", img: "right_middle_sleeve_image", ph: "right_middle_sleeve_placeholder", max: 15, price_text: 10, price_img: 15 },
    right_sleeve_bottom: { label: "Right Sleeve Bottom",view: "left",  text: "right_bottom_sleeve_text", text1: "right_bottom_sleeve_text1", img: "right_bottom_sleeve_image", ph: "right_bottom_sleeve_placeholder", max: 15, price_text: 10, price_img: 15 },
    left_sleeve_top:     { label: "Left Sleeve Top",    view: "right", text: "left_sleeve_text",         text1: "left_sleeve_text1",         img: "left_sleeve_image",         ph: "left_sleeve_placeholder",         max: 15, price_text: 10, price_img: 15 },
    left_sleeve_middle:  { label: "Left Sleeve Middle", view: "right", text: "left_middle_sleeve_text",  text1: "left_middle_sleeve_text1",  img: "left_middle_sleeve_image",  ph: "left_middle_sleeve_placeholder", max: 15, price_text: 10, price_img: 15 },
    left_sleeve_bottom:  { label: "Left Sleeve Bottom", view: "right", text: "left_bottom_sleeve_text",  text1: "left_bottom_sleeve_text1",  img: "left_bottom_sleeve_image",  ph: "left_bottom_sleeve_placeholder", max: 15, price_text: 10, price_img: 15 }
  };

  Object.keys(PLACEMENTS).forEach(function (k) {
    state.placements[k] = {
      mode: "none", text: "", font: "Varsity", size: 18, layout: "standard",
      fill: "#FFFFFF", stroke1: "#000000", stroke2: "",
      image: null, imageFile: null, imageName: "", artNote: "",
      x: 0, y: 0, rot: 0, scale: 100,
    };
  });

  /* ---------- Size data (exact competitor data attributes) ---------- */
  var SIZES = [
    { l: "XS", price: 5,  m: [117,112,62,57], w: [107,107,60,57] },
    { l: "S",  price: 5,  m: [122,117,65,60], w: [112,112,62,60] },
    { l: "M",  price: 10, m: [127,122,67,62], w: [117,117,65,62] },
    { l: "L",  price: 15, m: [132,127,70,65], w: [122,122,67,65] },
    { l: "XL", price: 20, m: [137,132,72,67], w: [127,127,70,67] },
    { l: "2XL",price: 25, m: [142,137,75,70], w: [132,132,72,70] },
    { l: "3XL",price: 30, m: [147,142,77,72], w: [137,137,75,72] },
    { l: "4XL",price: 35, m: [152,147,80,75], w: [142,142,77,75] },
    { l: "5XL",price: 40, m: [157,152,82,77], w: [147,147,80,77] },
    { l: "Custom", price: 45, m: null, w: null }
  ];

  /* ---------- Color palettes (exact competitor palette) ---------- */
  var PALETTE = [
    ["Army Green","#4b5320"],["Black","#000000"],["White","#FFFFFF"],["Grey","#868482"],
    ["Charcoal Grey","#212120"],["Cream","#F3ECDE"],["Forest Green","#228B22"],["Navy Blue","#010b2e"],
    ["Royal Blue","#00248f"],["Scarlet Red","#fe2400"],["Maroon","#8e1c2f"],["Burgundy","#800020"],
    ["Gold","#DEC03D"],["Purple","#8F2273"],["Pink","#F6A9C3"],["Hot Pink","#FF4D80"],["Brown","#964B00"]
  ];
  var ZIP_PALETTE = [["Gold","#d4af37"],["Silver","#c0c0c0"],["Black","#000000"],["Antique Brass","#8c7853"],["White","#FFFFFF"]];

  var FONTS = ["Varsity","Old English Text MT","Impact","Georgia","Times New Roman","Arial","Courier New","Brush Script MT","Verdana"];

  /* ---------- Helpers ---------- */
  function $(sel, ctx) { return (ctx || root).querySelector(sel); }
  function $all(sel, ctx) { return Array.prototype.slice.call((ctx || root).querySelectorAll(sel)); }
  function byId(id) { return root.querySelector("#" + id) || document.getElementById(id); }
  function css(nodes, styles) { nodes.forEach(function (n) { Object.keys(styles).forEach(function (k) { n.style[k] = styles[k]; }); }); }
  function svgAll(cls) { return $all(".bjc-view svg ." + cls); }

  function applySvgState() {
    var isZipper = state.closure === "zipper";
    var hasButtonPockets = state.pocket_style === "btn_pock";
    if (isZipper) {
      css(svgAll("zipper"), { display: "block" });
      css(svgAll("jacket_button").concat(svgAll("flap")), { display: "none" });
      css(svgAll("zip"), { display: "block" });
    } else {
      css(svgAll("jacket_button").concat(svgAll("zipper")).concat(svgAll("button_line")), { display: "none" });
      css(svgAll("flap"), { display: "block" });
    }
    css(svgAll("pocket2"), { display: hasButtonPockets ? "block" : "none" });
    css(svgAll("pock_button"), { display: hasButtonPockets ? "block" : "none" });

    if (state.colors.body.hex) {
      css(svgAll("body").concat(svgAll("sleeve"), svgAll("jacket_pockets"), svgAll("pocket2"),
        svgAll("standup_collar"), svgAll("bottom"), svgAll("sleeve_end"), svgAll("flap_color")), { fill: state.colors.body.hex });
    }
    if (state.colors.pockets.hex) css(svgAll("jacket_pockets").concat(svgAll("pocket2")), { fill: state.colors.pockets.hex });
    if (state.colors.collar.hex) css(svgAll("standup_collar"), { fill: state.colors.collar.hex });
    if (state.colors.trim_base.hex) css(svgAll("standup_collar").concat(svgAll("bottom"), svgAll("sleeve_end")), { fill: state.colors.trim_base.hex });
    if (state.colors.trim.hex) css(svgAll("trim"), { fill: state.colors.trim.hex });
    if (state.colors.zip.hex) css(svgAll("zip"), { fill: state.colors.zip.hex });
    if (state.colors.buttons.hex) css(svgAll("jacket_button"), { fill: state.colors.buttons.hex });

    $all(".bjc-view svg rect[id$='_object_box']").forEach(function (node) {
      node.style.display = guidesOn ? "block" : "none";
      node.setAttribute("stroke-width", guidesOn ? "0.6" : "0");
      node.setAttribute("stroke-dasharray", "2,2");
    });
    $all(".bjc-view svg g[id$='_placeholder']").forEach(function (node) {
      node.style.display = guidesOn ? "block" : "none";
    });
    Object.keys(PLACEMENTS).forEach(renderPlacement);
  }

  function hydrateView(viewName) {
    var view = root.querySelector('.bjc-view[data-view="' + viewName + '"]');
    var template = view && view.querySelector("template[data-bjc-view-template]");
    if (template) {
      view.replaceChildren(template.content.cloneNode(true));
      applySvgState();
    }
    preparePlacementTargets(view);
    return view;
  }

  function setStatus(msg, isErr, shouldFocus) {
    var status = byId("bjc-status");
    if (!status) return;
    status.textContent = msg;
    status.hidden = false;
    status.classList.toggle("err", !!isErr);
    if (shouldFocus) status.focus();
  }

  function toast(msg, isErr) {
    var t = $(".bjc-toast");
    t.textContent = msg;
    t.classList.toggle("err", !!isErr);
    t.classList.add("show");
    clearTimeout(t._h);
    t._h = setTimeout(function () { t.classList.remove("show"); }, 2800);
  }

  /* ---------- Fixed Shopify product price ---------- */
  function calcPrice() {
    var price = formatConfiguredPrice(CFG);
    var oldPrice = $(".bjc-price .str");
    var currentPrice = $(".bjc-price .dis") || $("[data-bjc-price]");
    if (oldPrice) oldPrice.hidden = true;
    if (currentPrice) currentPrice.textContent = price;
    return price;
  }

  /* ---------- View switching ---------- */
  function showView(v) {
    hydrateView(v);
    $all(".bjc-view").forEach(function (el) { el.classList.toggle("active", el.dataset.view === v); });
    $all(".bjc-thumb").forEach(function (el) {
      var active = el.dataset.view === v;
      el.classList.toggle("active", active);
      el.setAttribute("aria-pressed", String(active));
    });
  }
  $all(".bjc-thumb").forEach(function (t) {
    t.addEventListener("click", function () { showView(t.dataset.view); });
  });

  /* ---------- Guidelines toggle ---------- */
  var guidesOn = false;
  var guidesButton = $(".bjc-guides");
  guidesButton.setAttribute("aria-checked", "false");
  guidesButton.addEventListener("click", function () {
    guidesOn = !guidesOn;
    var switchVisual = $(".bjc-switch", guidesButton);
    if (switchVisual) switchVisual.classList.toggle("on", guidesOn);
    guidesButton.setAttribute("aria-checked", String(guidesOn));
    $all(".bjc-view svg rect[id$='_object_box']").forEach(function (r) {
      r.style.display = guidesOn ? "block" : "none";
      r.setAttribute("stroke-width", guidesOn ? "0.6" : "0");
      r.setAttribute("stroke-dasharray", "2,2");
    });
    $all(".bjc-view svg g[id$='_placeholder']").forEach(function (g) {
      g.style.display = guidesOn ? "block" : "none";
    });
  });

  /* ---------- Accordions ---------- */
  function setAccordionOpen(acc, open) {
    acc.classList.toggle("open", open);
    var button = acc.querySelector(":scope > .bjc-acc-btn");
    if (button) button.setAttribute("aria-expanded", String(open));
  }

  $all(".bjc-acc-btn").forEach(function (btn) {
    btn.setAttribute("aria-expanded", String(btn.parentElement.classList.contains("open")));
    btn.addEventListener("click", function () {
      var acc = btn.parentElement;
      var isSub = acc.classList.contains("bjc-sub");
      var siblings = $all(isSub ? ".bjc-sub" : ".bjc-acc:not(.bjc-sub)", acc.parentElement);
      var wasOpen = acc.classList.contains("open");
      siblings.forEach(function (s) { setAccordionOpen(s, false); });
      if (!wasOpen) setAccordionOpen(acc, true);
    });
  });

  /* ---------- Option buttons (gender/material/closure/pocket) ---------- */
  function bindOpts(groupSel, cb) {
    $all(groupSel + " .bjc-opt").forEach(function (b) {
      b.addEventListener("click", function () {
        $all(groupSel + " .bjc-opt").forEach(function (x) {
          x.classList.remove("sel");
          x.setAttribute("aria-pressed", "false");
        });
        b.classList.add("sel");
        b.setAttribute("aria-pressed", "true");
        cb(b);
      });
    });
  }

  bindOpts("[data-group=gender]", function (b) {
    state.gender = b.dataset.value;
    renderSizeDetails();
  });
  bindOpts("[data-group=material]", function (b) {
    state.material = b.dataset.value;
  });
  bindOpts("[data-group=closure]", function (b) {
    state.closure = b.dataset.value;
    // exact competitor show/hide logic
    if (state.closure === "zipper") {
      css(svgAll("zipper"), { display: "block" });
      css(svgAll("jacket_button").concat(svgAll("flap")), { display: "none" });
      css(svgAll("zip"), { display: "block" });
    } else {
      css(svgAll("jacket_button").concat(svgAll("zipper")).concat(svgAll("button_line")), { display: "none" });
      css(svgAll("flap"), { display: "block" });
    }
  });
  bindOpts("[data-group=pocket]", function (b) {
    state.pocket_style = b.dataset.value;
    var bomber = state.pocket_style === "btn_pock";
    css(svgAll("pocket2"), { display: bomber ? "block" : "none" });
    css(svgAll("pock_button"), { display: bomber ? "block" : "none" });
  });

  /* ---------- Color swatch rendering + exact recolor handlers ---------- */
  function buildSwatches(containerId, palette, onPick) {
    var box = byId(containerId);
    if (!box) return;
    palette.forEach(function (p) {
      var d = document.createElement("button");
      d.type = "button";
      d.className = "bjc-swatch";
      d.title = p[0];
      d.setAttribute("aria-label", p[0]);
      d.setAttribute("aria-pressed", "false");
      d.dataset.title = p[0];
      d.dataset.value = p[1];
      d.style.backgroundColor = p[1];
      d.addEventListener("click", function () {
        $all(".bjc-swatch", box).forEach(function (s) { s.classList.remove("sel"); s.setAttribute("aria-pressed", "false"); });
        d.classList.add("sel");
        d.setAttribute("aria-pressed", "true");
        onPick(p[1], p[0]);
      });
      box.appendChild(d);
    });
  }

  // BODY — exact: .body,.sleeve,.jacket_pockets,.pocket2,.standup_collar,.bottom,.sleeve_end,.flap_color
  buildSwatches("bjc-sw-body", PALETTE, function (hex, name) {
    css(svgAll("body").concat(svgAll("sleeve"), svgAll("jacket_pockets"), svgAll("pocket2"),
      svgAll("standup_collar"), svgAll("bottom"), svgAll("sleeve_end"), svgAll("flap_color")), { fill: hex });
    css(svgAll("pock_stroke"), { stroke: "#8a9ba8", strokeWidth: "0.516" });
    css(svgAll("dash"), { stroke: "#8a9ba8", fill: "#8a9ba8", strokeWidth: "0.516", strokeDasharray: "0,1" });
    byId("bjc-chip-body").style.backgroundColor = hex;
    byId("bjc-name-body").textContent = "(" + name + ")";
    state.colors.body = { hex: hex, name: name };
  });

  // POCKETS — exact: .jacket_pockets,.pocket2
  buildSwatches("bjc-sw-pockets", PALETTE, function (hex, name) {
    css(svgAll("jacket_pockets").concat(svgAll("pocket2")), { fill: hex });
    css(svgAll("pock_button"), { fill: "transparent" });
    css(svgAll("pock_stroke"), { stroke: "#8a9ba8", strokeWidth: "0.516" });
    css(svgAll("dash"), { stroke: "#8a9ba8", fill: "#8a9ba8", strokeWidth: "0.516", strokeDasharray: "0,1" });
    byId("bjc-chip-pockets").style.backgroundColor = hex;
    byId("bjc-name-pockets").textContent = "(" + name + ")";
    state.colors.pockets = { hex: hex, name: name };
  });

  // COLLAR — exact: .standup_collar
  buildSwatches("bjc-sw-collar", PALETTE, function (hex, name) {
    css(svgAll("standup_collar"), { fill: hex });
    byId("bjc-chip-collar").style.backgroundColor = hex;
    byId("bjc-name-collar").textContent = "(" + name + ")";
    state.colors.collar = { hex: hex, name: name };
  });

  // TRIM BASE — exact: .standup_collar,.bottom,.sleeve_end
  buildSwatches("bjc-sw-trimbase", PALETTE, function (hex, name) {
    css(svgAll("standup_collar").concat(svgAll("bottom"), svgAll("sleeve_end")), { fill: hex });
    byId("bjc-chip-trimbase").style.backgroundColor = hex;
    byId("bjc-name-trimbase").textContent = "(" + name + ")";
    state.colors.trim_base = { hex: hex, name: name };
  });

  // TRIM STRIPES — exact: .trim
  buildSwatches("bjc-sw-trim", PALETTE, function (hex, name) {
    css(svgAll("trim"), { fill: hex });
    byId("bjc-chip-trim").style.backgroundColor = hex;
    byId("bjc-name-trim").textContent = "(" + name + ")";
    state.colors.trim = { hex: hex, name: name };
  });

  // ZIP — exact: .zip
  buildSwatches("bjc-sw-zip", ZIP_PALETTE, function (hex, name) {
    css(svgAll("zip"), { fill: hex });
    byId("bjc-chip-zip").style.backgroundColor = hex;
    byId("bjc-name-zip").textContent = "(" + name + ")";
    state.colors.zip = { hex: hex, name: name };
  });

  // BUTTONS — exact: .jacket_button
  buildSwatches("bjc-sw-buttons", PALETTE, function (hex, name) {
    css(svgAll("jacket_button"), { fill: hex });
    byId("bjc-chip-buttons").style.backgroundColor = hex;
    byId("bjc-name-buttons").textContent = "(" + name + ")";
    state.colors.buttons = { hex: hex, name: name };
  });

  /* ---------- Design placements ---------- */
  var dsgHost = byId("bjc-designs");
  Object.keys(PLACEMENTS).forEach(function (key) {
    var P = PLACEMENTS[key];
    var acc = document.createElement("div");
    acc.className = "bjc-acc bjc-sub";
    acc.dataset.placement = key;
    acc.innerHTML =
      '<button type="button" class="bjc-acc-btn">' + P.label + "</button>" +
      '<div class="bjc-acc-body">' +
      '<div class="bjc-dsg-tabs">' +
      '<button type="button" class="bjc-dsg-tab sel" data-mode="none">No Design</button>' +
      '<button type="button" class="bjc-dsg-tab" data-mode="text">Name</button>' +
      '<button type="button" class="bjc-dsg-tab" data-mode="letter">Letter</button>' +
      '<button type="button" class="bjc-dsg-tab" data-mode="image">Image</button>' +
      "</div>" +
      '<div class="bjc-dsg-fields" data-fields="text">' +
      '<div class="bjc-field"><label>Name <span class="bjc-count">0/' + P.max + "</span></label>" +
      '<input type="text" maxlength="' + P.max + '" data-f="text" placeholder="Enter text"></div>' +
      '<div class="bjc-row2">' +
      '<div class="bjc-field"><label>Font</label><select data-f="font">' +
      FONTS.map(function (f) { return "<option>" + f + "</option>"; }).join("") + "</select></div>" +
      '<div class="bjc-field"><label>Font Size</label><input type="number" data-f="size" value="18" min="8" max="60"></div>' +
      "</div>" +
      '<div class="bjc-mini-label">Fill / Stroke Colors</div>' +
      '<div class="bjc-row2">' +
      '<div class="bjc-field"><label>Fill</label><input type="color" data-f="fill" value="#FFFFFF" style="width:100%;height:30px;border:1px solid #ccc;border-radius:3px"></div>' +
      '<div class="bjc-field"><label>Stroke 1</label><input type="color" data-f="stroke1" value="#000000" style="width:100%;height:30px;border:1px solid #ccc;border-radius:3px"></div>' +
      '<div class="bjc-field"><label>Stroke 2</label><input type="color" data-f="stroke2" value="#000000" style="width:100%;height:30px;border:1px solid #ccc;border-radius:3px"></div>' +
      "</div>" +
      "</div>" +
      '<div class="bjc-dsg-fields" data-fields="image">' +
      '<div class="bjc-field"><label>Upload Image</label>' +
      '<input type="file" data-f="file" accept=".png,.jpg,.jpeg,.webp,.pdf" style="font-size:11px"></div>' +
      '<div class="bjc-file-name"></div>' +
      '<div class="bjc-field"><label>Artwork Note</label><input type="text" data-f="artnote" placeholder="Size / placement notes"></div>' +
      '<p class="bjc-upload-note">PNG, JPG, WEBP or PDF up to 5 MB. Artwork filename will appear in the cart item details.</p>' +
      "</div>" +
      '<div class="bjc-dsg-fields" data-fields="pos">' +
      '<div class="bjc-row2">' +
      '<div class="bjc-field"><label>Horizontal</label><input type="range" data-f="x" min="-40" max="40" value="0"></div>' +
      '<div class="bjc-field"><label>Vertical</label><input type="range" data-f="y" min="-40" max="40" value="0"></div>' +
      "</div>" +
      '<div class="bjc-row2">' +
      '<div class="bjc-field"><label>Rotation</label><input type="range" data-f="rot" min="-180" max="180" value="0"></div>' +
      '<div class="bjc-field"><label>Scale %</label><input type="range" data-f="scale" min="40" max="220" value="100"></div>' +
      "</div>" +
      "</div>" +
      "</div>";
    dsgHost.appendChild(acc);
    acc.querySelector(".bjc-acc-btn").setAttribute("aria-expanded", "false");

    var st = state.placements[key];

    acc.querySelector(".bjc-acc-btn").addEventListener("click", function () {
      var open = acc.classList.contains("open");
      $all(".bjc-sub", dsgHost).forEach(function (s) { setAccordionOpen(s, false); });
      if (!open) { setAccordionOpen(acc, true); showView(P.view); }
    });

    function fields(name) { return acc.querySelector('[data-fields="' + name + '"]'); }
    function updateVisibility() {
      fields("text").classList.toggle("show", st.mode === "text" || st.mode === "letter");
      fields("image").classList.toggle("show", st.mode === "image");
      fields("pos").classList.toggle("show", st.mode !== "none");
    }

    $all(".bjc-dsg-tab", acc).forEach(function (tab) {
      tab.addEventListener("click", function () {
        $all(".bjc-dsg-tab", acc).forEach(function (t) { t.classList.remove("sel"); t.setAttribute("aria-pressed", "false"); });
        tab.classList.add("sel");
        tab.setAttribute("aria-pressed", "true");
        st.mode = tab.dataset.mode;
        if (st.mode === "letter") {
          acc.querySelector('[data-f="text"]').maxLength = 2;
          acc.querySelector('[data-f="size"]').value = 34;
          st.size = 34;
        } else if (st.mode === "text") {
          acc.querySelector('[data-f="text"]').maxLength = P.max;
        }
        updateVisibility();
        renderPlacement(key);
        calcPrice();
      });
    });

    acc.addEventListener("input", function (e) {
      var f = e.target.dataset.f;
      if (!f) return;
      if (f === "text") {
        st.text = e.target.value;
        acc.querySelector(".bjc-count").textContent = st.text.length + "/" + e.target.maxLength;
      } else if (f === "font") st.font = e.target.value;
      else if (f === "size") st.size = parseInt(e.target.value, 10) || 18;
      else if (f === "fill") st.fill = e.target.value;
      else if (f === "stroke1") st.stroke1 = e.target.value;
      else if (f === "stroke2") st.stroke2 = e.target.value;
      else if (f === "artnote") st.artNote = e.target.value;
      else if (f === "x") st.x = parseInt(e.target.value, 10);
      else if (f === "y") st.y = parseInt(e.target.value, 10);
      else if (f === "rot") st.rot = parseInt(e.target.value, 10);
      else if (f === "scale") st.scale = parseInt(e.target.value, 10);
      renderPlacement(key);
      calcPrice();
    });

    acc.querySelector('[data-f="file"]').addEventListener("change", function () {
      var file = this.files[0];
      if (!file) return;
      var fileError = artworkFileError(file);
      if (fileError) {
        st.imageFile = null;
        st.imageName = "";
        toast(fileError, true);
        setStatus(fileError, true, true);
        this.value = "";
        return;
      }
      st.imageFile = file;
      st.imageName = file.name;
      acc.querySelector(".bjc-file-name").textContent = "✓ " + file.name;
      if (/\.(png|jpe?g|webp)$/i.test(file.name)) {
        var reader = new FileReader();
        reader.onload = function (ev) { st.image = ev.target.result; renderPlacement(key); calcPrice(); };
        reader.readAsDataURL(file);
      } else {
        st.image = null; // pdf — filename recorded only
        renderPlacement(key);
      }
    });
  });

  function placementKeyFromTarget(target) {
    var node = target;
    while (node && node !== root) {
      if (node.id) {
        var match = Object.keys(PLACEMENTS).filter(function (key) {
          var prefix = PLACEMENTS[key].ph.replace(/_placeholder$/, "");
          return node.id === PLACEMENTS[key].ph || node.id === prefix + "_object_box" || node.id === prefix + "_object_box1";
        })[0];
        if (match) return match;
      }
      node = node.parentElement;
    }
    return null;
  }

  function openPlacement(key) {
    var P = PLACEMENTS[key];
    var acc = dsgHost.querySelector('[data-placement="' + key + '"]');
    if (!P || !acc) return;
    showView(P.view);
    var designAccordion = dsgHost.closest(".bjc-acc");
    if (designAccordion) setAccordionOpen(designAccordion, true);
    $all(".bjc-sub", dsgHost).forEach(function (s) { setAccordionOpen(s, s === acc); });
    if (state.placements[key].mode === "none") acc.querySelector('[data-mode="text"]').click();
    var input = acc.querySelector('[data-f="text"]');
    if (input) input.focus();
  }

  function preparePlacementTargets(context) {
    $all("[id$='_object_box'],[id$='_object_box1'],[id$='_placeholder']", context || root).forEach(function (node) {
      var key = placementKeyFromTarget(node);
      if (!key) return;
      node.setAttribute("role", "button");
      node.setAttribute("tabindex", "0");
      node.setAttribute("aria-label", "Customize " + PLACEMENTS[key].label);
    });
  }

  root.addEventListener("click", function (event) {
    var key = placementKeyFromTarget(event.target);
    if (key) openPlacement(key);
  });
  root.addEventListener("keydown", function (event) {
    if (event.key !== "Enter" && event.key !== " ") return;
    var key = placementKeyFromTarget(event.target);
    if (!key) return;
    event.preventDefault();
    openPlacement(key);
  });

  /* ---------- Render a placement onto the SVG (exact id-based like competitor) ---------- */
  function renderPlacement(key) {
    var P = PLACEMENTS[key], st = state.placements[key];
    var tBack = byId(P.text), tFront = byId(P.text1), img = byId(P.img);
    if (!tFront) return;

    var showText = (st.mode === "text" || st.mode === "letter") && st.text.trim() !== "";
    var showImg = st.mode === "image" && st.image;

    [tBack, tFront].forEach(function (t) {
      if (!t) return;
      t.textContent = st.text;
      t.style.display = showText ? "block" : "none";
      t.style.fontFamily = st.font;
      t.style.fontSize = st.size + "px";
      var bx = parseFloat(t.getAttribute("x")), by = parseFloat(t.getAttribute("y"));
      t.setAttribute("transform",
        "translate(" + st.x + " " + st.y + ") rotate(" + st.rot + " " + bx + " " + by + ") " +
        "translate(" + bx + " " + by + ") scale(" + st.scale / 100 + ") translate(" + (-bx) + " " + (-by) + ")");
    });
    if (tFront) { tFront.style.fill = st.fill; tFront.style.stroke = st.stroke1; }
    if (tBack) { tBack.style.stroke = st.stroke2 || "none"; }

    if (img) {
      if (showImg) {
        img.setAttribute("href", st.image);
        img.setAttributeNS("http://www.w3.org/1999/xlink", "xlink:href", st.image);
        img.style.display = "block";
        var ix = parseFloat(img.getAttribute("x")), iy = parseFloat(img.getAttribute("y"));
        var iw = parseFloat(img.getAttribute("width")), ih = parseFloat(img.getAttribute("height"));
        var cx = ix + iw / 2, cy = iy + ih / 2;
        img.setAttribute("transform",
          "translate(" + st.x + " " + st.y + ") rotate(" + st.rot + " " + cx + " " + cy + ") " +
          "translate(" + cx + " " + cy + ") scale(" + st.scale / 100 + ") translate(" + (-cx) + " " + (-cy) + ")");
      } else {
        img.style.display = "none";
      }
    }

  }

  /* ---------- Sizes ---------- */
  var sizeHost = byId("bjc-sizes");
  var enabledSizes = availableSizes(SIZES.map(function (s) { return s.l; }), CFG);
  SIZES.filter(function (s) { return enabledSizes.indexOf(s.l) !== -1; }).forEach(function (s) {
    var b = document.createElement("button");
    b.type = "button";
    b.className = "bjc-opt";
    b.textContent = s.l;
    b.setAttribute("aria-pressed", "false");
    b.addEventListener("click", function () {
      $all(".bjc-opt", sizeHost).forEach(function (x) { x.classList.remove("sel"); x.setAttribute("aria-pressed", "false"); });
      b.classList.add("sel");
      b.setAttribute("aria-pressed", "true");
      state.size = s.l;
      byId("bjc-custom-size").classList.toggle("show", s.l === "Custom");
      renderSizeDetails();
    });
    sizeHost.appendChild(b);
  });

  function renderSizeDetails() {
    var d = byId("bjc-size-details");
    var s = SIZES.filter(function (x) { return x.l === state.size; })[0];
    if (!s || !s.m) { d.classList.remove("show"); return; }
    var arr = (state.gender === "women") ? s.w : s.m;
    d.innerHTML = "<b>" + state.size + " (" + state.gender + "):</b> Chest " + arr[0] + "cm · Bottom " + arr[1] + "cm · Back " + arr[2] + "cm · Sleeves " + arr[3] + "cm";
    d.classList.add("show");
  }

  ["chest", "bottom", "back", "sleeves"].forEach(function (f) {
    var inp = byId("bjc-cs-" + f);
    if (inp) inp.addEventListener("input", function () { state.custom_size[f] = inp.value; });
  });

  /* ---------- Full-width size chart ---------- */
  var chartPanel = byId("bjc-size-chart-panel");
  var chartTriggers = [byId("bjc-size-chart-trigger"), byId("bjc-size-chart-link")];
  var chartUnit = "cm";
  var chartSizes = SIZES.filter(function (size) {
    return size.m && size.w && enabledSizes.indexOf(size.l) !== -1;
  });

  function renderSizeChart() {
    [["men", "m"], ["women", "w"]].forEach(function (gender) {
      var table = chartPanel.querySelector('[data-bjc-chart="' + gender[0] + '"] table');
      table.querySelectorAll("thead,tbody").forEach(function (node) { node.remove(); });
      var head = table.createTHead().insertRow();
      var corner = document.createElement("th");
      corner.scope = "col";
      corner.textContent = "Sizes";
      head.appendChild(corner);
      chartSizes.forEach(function (size) {
        var cell = document.createElement("th");
        cell.scope = "col";
        cell.textContent = size.l;
        head.appendChild(cell);
      });
      var body = table.createTBody();
      [["Chest", 0], ["Bottom", 1], ["Back length", 2], ["Sleeve length", 3]].forEach(function (measure) {
        var row = body.insertRow();
        var label = document.createElement("th");
        label.scope = "row";
        label.textContent = measure[0];
        row.appendChild(label);
        chartSizes.forEach(function (size) {
          row.insertCell().textContent = formatMeasurement(size[gender[1]][measure[1]], chartUnit);
        });
      });
    });
  }

  chartTriggers.forEach(function (trigger) {
    trigger.addEventListener("click", function () {
      chartPanel.hidden = !chartPanel.hidden;
      chartTriggers.forEach(function (button) { button.setAttribute("aria-expanded", String(!chartPanel.hidden)); });
      if (!chartPanel.hidden) chartPanel.scrollIntoView({ behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth", block: "start" });
    });
  });
  $all("[data-bjc-unit]", chartPanel).forEach(function (button) {
    button.addEventListener("click", function () {
      chartUnit = button.dataset.bjcUnit;
      $all("[data-bjc-unit]", chartPanel).forEach(function (item) {
        item.setAttribute("aria-pressed", String(item === button));
      });
      renderSizeChart();
    });
  });
  renderSizeChart();

  $all(".bjc-modal").forEach(function (m) {
    m.addEventListener("click", function (e) {
      if (e.target !== m && !e.target.classList.contains("bjc-modal-close")) return;
      if (typeof m.close === "function") m.close();
      else m.classList.remove("show");
    });
  });

  /* ---------- Note + qty ---------- */
  byId("bjc-note").addEventListener("input", function () { state.note = this.value; });
  byId("bjc-qty").addEventListener("input", function () { state.qty = this.value; });

  /* ---------- Validation (exact competitor rules) ---------- */
  function validate() {
    return validateOrder(state, CFG);
  }

  /* ---------- Build line item properties ---------- */
  function buildProperties() {
    var props = {
      "Gender": state.gender,
      "Material": state.material,
      "Front Closure": state.closure === "zipper" ? "Zipper" : "Zip + Flap",
      "Pocket Style": state.pocket_style === "btn_pock" ? "Bomber" : "Slash",
      "Size": state.size
    };
    if (state.size === "Custom") {
      props["Custom Chest"] = state.custom_size.chest + " cm";
      props["Custom Bottom"] = state.custom_size.bottom + " cm";
      props["Custom Back Length"] = state.custom_size.back + " cm";
      props["Custom Sleeves"] = state.custom_size.sleeves + " cm";
    }
    Object.keys(state.colors).forEach(function (k) {
      if (state.colors[k].name) props["Color — " + k.replace(/_/g, " ")] = state.colors[k].name + " (" + state.colors[k].hex + ")";
    });
    Object.keys(state.placements).forEach(function (k) {
      var st = state.placements[k], P = PLACEMENTS[k];
      if (st.mode === "text" || st.mode === "letter") {
        if (st.text.trim()) props[P.label] = '"' + st.text + '" · ' + st.font + " " + st.size + "px · fill " + st.fill +
          " · pos(" + st.x + "," + st.y + ") rot " + st.rot + "° scale " + st.scale + "%";
      }
    });
    if (state.note.trim()) props["Special Note"] = state.note.trim();
    return props;
  }

  function collectArtwork() {
    return Object.keys(state.placements).reduce(function (files, key) {
      var st = state.placements[key];
      if (st.mode === "image" && st.imageFile) {
        files.push({ label: PLACEMENTS[key].label, file: st.imageFile, filename: st.imageName, note: st.artNote.trim() });
      }
      return files;
    }, []);
  }

  function cartSectionIds() {
    return $all("cart-items-component[data-section-id]", document).map(function (item) {
      return item.dataset.sectionId;
    }).filter(Boolean);
  }

  function setAdding(adding) {
    [byId("bjc-add-cart"), byId("bjc-add-another")].forEach(function (button) {
      if (!button) return;
      if (!button.dataset.label) button.dataset.label = button.textContent;
      button.disabled = adding;
      button.classList.toggle("loading", adding);
      if (button.id === "bjc-add-cart") button.textContent = adding ? "Adding…" : button.dataset.label;
    });
  }

  /* ---------- Add to Cart (Shopify AJAX) ---------- */
  function addToCart(andCheckout) {
    var errors = validate();
    if (errors.length) { toast(errors[0], true); setStatus(errors[0], true, true); return; }
    var variant = selectVariant(state.size, CFG);
    var quantity = Number(state.qty);
    setAdding(true);

    if (!variant) {
      // Preview mode — no Shopify product bound
      setAdding(false);
      toast("Preview mode: configure the product in Shopify to enable cart.", true);
      setStatus("This customizer is not connected to an available product variant.", true, true);
      var summary = byId("bjc-summary-modal");
      if (typeof summary.showModal === "function") summary.showModal();
      else summary.classList.add("show");
      var tbl = byId("bjc-summary-table");
      var previewProperties = buildProperties();
      tbl.replaceChildren();
      Object.keys(previewProperties).forEach(function (key) {
        var row = tbl.insertRow();
        row.insertCell().textContent = key;
        row.insertCell().textContent = previewProperties[key];
      });
      return;
    }

    var payload = createCartFormData({
      variantId: variant.id,
      quantity: quantity,
      properties: buildProperties(),
      artwork: collectArtwork(),
      sectionIds: cartSectionIds(),
      sectionsUrl: window.location.pathname + window.location.search
    });
    if (!window.OrlavenCart) {
      setAdding(false);
      setStatus("Your bag is still loading. Please try again in a moment.", true, true);
      return;
    }
    window.OrlavenCart.add(payload, "Custom jacket added to your bag.")
      .then(function (result) {
        toast("✓ Custom jacket added to cart!");
        setStatus(result.cart ? "Custom jacket added to cart." :
          "Custom jacket was added. Open your bag to confirm its contents.", !result.cart, false);
      })
      .catch(function (error) {
        toast(error.message || "Could not add to cart", true);
        setStatus(error.message || "Could not add to cart.", true, true);
      })
      .then(function () { setAdding(false); });
  }

  byId("bjc-add-cart").addEventListener("click", function () { addToCart(false); });
  byId("bjc-add-another").addEventListener("click", function () {
    addToCart(false);
  });

  /* ---------- Save design on device (like orlaven) ---------- */
  byId("bjc-save").addEventListener("click", function () {
    try {
      var copy = JSON.parse(JSON.stringify(state));
      Object.keys(copy.placements).forEach(function (k) { copy.placements[k].image = null; });
      localStorage.setItem("bjc_saved_design", JSON.stringify(copy));
      toast("✓ Design saved on this device");
    } catch (e) { toast("Could not save design", true); }
  });

  (function restore() {
    try {
      var raw = localStorage.getItem("bjc_saved_design");
      if (!raw) return;
      // silent availability — user can manually re-apply if desired in future version
    } catch (e) { }
  })();

  /* ---------- Init defaults (match competitor initial state) ---------- */
  applySvgState();
  showView("front");
  calcPrice();
})();
